import { useEffect, useState, useContext } from "react";
import { Box, Typography, Button } from "@mui/material";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import { useFetchAdvisorEngagementReports } from "services/engagement";
import AddFamilyDialog from "components/family/AddFamilyDialog";
import { ReactComponent as PlusIcon } from "../icons/Plus.svg";
import useTitle from "components/hooks/useTitle";
import { FamilyStatus } from "../protogen/advisors_service_pb";
import { AdvisorReportRow } from "../components/engagement/AdvisorReportTable";
import ColumnHeader from "../components/engagement/ColumnHeader";
import { sortFamiliesByFields } from "../components/engagement/utils";
import { useSearchParams } from "react-router-dom";
import { Family, Member } from "components/family/types";
import { format } from "date-fns";
import { useFetchAdvisorProfile } from "services/advisor";
import { CurrentUserContext } from "components/context/RequireAuth";

export default () => {
  const isMobile = useIsMobile();
  useTitle("Families");
  const currentUser = useContext(CurrentUserContext);
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [sorts, setSorts] = useState<{ field: string; asc: boolean }[]>([
    // Note the order is how they will be applied, so the first entry is the primary sort.
    { field: "active", asc: false },
    { field: "state", asc: true },
    { field: "familyName", asc: true },
  ]);
  const { request, data, loading } = useFetchAdvisorEngagementReports();
  const { request: fetchAdvisorProfile } = useFetchAdvisorProfile();

  useEffect(() => {
    request();
  }, []);
  const activatedStatuses = new Set([
    FamilyStatus.ACTIVE,
    FamilyStatus.PROSPECT,
    FamilyStatus.PREACTIVATION,
    FamilyStatus.DEMO,
    FamilyStatus.TEST,
  ]);
  const allReports = sortFamiliesByFields(data?.reports || [], sorts).filter(
    (r) => activatedStatuses.has(r.family?.status),
  );
  const otherReports = sortFamiliesByFields(data?.reports || [], sorts).filter(
    (r) => !activatedStatuses.has(r.family?.status),
  );
  const [testFamilyData, setTestFamilyData] = useState<
    | {
        family: Partial<Family>;
        members: Partial<Member>[];
      }
    | undefined
  >(undefined);

  const setupTestFamily = async () => {
    const resp = await fetchAdvisorProfile({
      advisorRef: currentUser?.ref,
    });
    if (!resp?.advisor) return;
    const advisorProfile = resp.advisor;
    const testEmail = advisorProfile?.personalEmail.replace("@", "+test@");
    setTestFamilyData({
      family: {
        name: `${advisorProfile?.lastName} (Test)`,
        status: FamilyStatus.TEST,
        startDate: format(new Date(), "MM/dd/yyyy"),
        isTestFamily: true,
        address: advisorProfile?.address,
      },
      members: [
        {
          firstName: advisorProfile?.firstName,
          lastName: advisorProfile?.lastName,
          email: testEmail,
          phone: advisorProfile?.personalPhone,
          timezone: advisorProfile?.timezone,
          activityNotificationEmailsEnabled: true,
        },
      ],
    });
    setModalOpen(true);
  };
  useEffect(() => {
    // Set test family data
    if (searchParams.get("addTestFamily") === "true") {
      setupTestFamily();
    }
  }, [searchParams]);

  return (
    <Box
      sx={{
        margin: isMobile ? "" : " auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "48px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Families</Typography>
            <Button startIcon={<PlusIcon />} onClick={() => setModalOpen(true)}>
              Add Family
            </Button>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          padding: !isMobile ? "24px 0" : undefined,
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          padding={"0 24px"}
          mr="34px"
        >
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Family"
            sortKey="familyName"
          />
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Advisor"
            sortKey="advisorName"
            hideMobile
          />
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Last activity"
            sortKey="lastActivity"
          />
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Utilization"
            sortKey="utilization"
            hideMobile
          />
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Renewal"
            sortKey="renewal"
            hideMobile
          />
          <ColumnHeader
            sorts={sorts}
            setSorts={setSorts}
            title="Start date"
            sortKey="startDate"
            hideMobile
          />
        </Box>
        {loading ? (
          <Loading />
        ) : allReports.length > 0 ? (
          allReports.map((report) => (
            <AdvisorReportRow key={report.family.ref} report={report} />
          ))
        ) : (
          <Box>No active families.</Box>
        )}
      </Box>
      {otherReports.length > 0 && (
        <Box display="flex" flexDirection="column" gap="16px" paddingTop="8px">
          <Typography variant="h6">Inactive Families</Typography>
          {otherReports.map((report) => (
            <AdvisorReportRow key={report.family.ref} report={report} />
          ))}
        </Box>
      )}
      <AddFamilyDialog
        // Force the dialog to remount when the test family data is set
        key={testFamilyData ? "x" : "xx"}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialFamilyData={testFamilyData?.family}
        initialMembers={testFamilyData?.members}
      />
    </Box>
  );
};
